import * as PIXI from 'pixi.js';

import { BufferingMode } from '../../../enums';
import type { SeekMode } from '../../../enums';
import type { Transformation as TransformationType } from '../../../types';
import { Config } from '../../../video-data/config';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';
import type { LoadSettings } from '../../serializableMixin';
import { DynamicStylingMixin } from '../../dynamicStylingMixin';
import type { BaseAssetSettings } from '../BaseAsset';
import { BaseAsset } from '../BaseAsset';
import { resetSprite } from '../utils';
import { HtmlCompositionRenderer } from './HtmlCompositionRenderer';
import { replaceEditableText } from './editableText';
import { computeEditableVisibility } from './editableVisibility';
import type { GsapTimelineLike } from './editableVisibility';
import { HtmlTextEditManager, EDITABLE_OVERLAY_STYLE_PROPS } from './HtmlTextEditManager';
import { lintHtmlComposition } from './lint/lintHtmlComposition';
import { runRuntimeLintRules } from './lint/runtimeRules';
import type { LintFinding } from './lint/types';
import { manageComposition, templateColorToHex, TEMPLATE_COLOR_VAR_PREFIX } from './htmlManagement';

type HtmlAssetSettings = BaseAssetSettings;

/** A user-editable text node (keyed by its element `id`), located at the preview/edit frame. Bounds are in reference-resolution space (0..width, 0..height). */
export interface EditableTextRegion {
    id: string;
    text: string;
    bounds: { x: number; y: number; width: number; height: number };
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

// Frame grid for the playback prefetch only — defines the next frame. Seek/export rasterize the
// exact progress and ignore it, so it never affects fidelity. 30 = the engine's de-facto fps.
const PLAYBACK_RASTER_FPS = 30;

/**
 * A scene asset whose content is an HTML + timeline composition authored outside Luminary. It loads
 * into a hidden iframe, is seeked by normalized progress, and is rasterized with snapDOM into a
 * PixiJS texture, so the editor preview and MP4 export share one path (like ChartAsset, but async,
 * so it plugs into the `_bufferAtTime` buffering seam). HTML is set via the external `html` setter;
 * Luminary lints it, injects the template-color palette as CSS variables, and owns the iframe.
 */
export class HtmlAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.HTML_ASSET_TYPE;

    #width: number;
    #height: number;

    #html = '';
    #managedHtml = '';
    #managedSourceHtml: string | null = null;
    #paletteKey = '';
    #textEditing = false;
    #managedTextEditing = false;
    #htmlTextEditManager: HtmlTextEditManager | null = null;

    #progress = 0;

    #sprite: PIXI.Sprite;
    #renderer: HtmlCompositionRenderer;

    #loadedManagedHtml: string | null = null;

    #displayedCanvas: HTMLCanvasElement | null = null;
    #preparedCanvas: HTMLCanvasElement | null = null;
    #preparedFrameIndex: number | null = null;
    #pendingFrameIndex: number | null = null;

    // Serialized: the single shared iframe can't rasterize concurrent frames.
    #rasterizeChain: Promise<void> = Promise.resolve();

    constructor(container: unknown, settings: HtmlAssetSettings) {
        super(container, settings);

        const { width, height } = this.getReferenceResolution();
        this.#width = width;
        this.#height = height;

        this.#renderer = new HtmlCompositionRenderer(this.#width, this.#height);
        this.#sprite = new PIXI.Sprite();
        this._addPixiChild(this.#sprite);
    }

    override destroy() {
        this.#renderer.destroy();
        this.#displayedCanvas = null;
        this.#preparedCanvas = null;
        this.#preparedFrameIndex = null;
        this.#pendingFrameIndex = null;
        super.destroy(false);
        this.#sprite.destroy(false);
    }

    override get _shouldLoad() {
        return true;
    }

    // Always loaded: per-frame readiness is gated by the awaited _bufferAtTime, not isLoaded.
    // Gating isLoaded on a rasterized frame would deadlock onAssetsLoaded (awaited before the
    // first seek has requested any frame).
    override get isLoaded(): boolean {
        return true;
    }

    override get failedToLoad(): boolean {
        return false;
    }

    /** @internal */
    _hasRenderedFrame(): boolean {
        return !!this.#displayedCanvas;
    }

    /** @internal */
    _preparedFrameIndex(): number | null {
        return this.#preparedFrameIndex;
    }

    override get width() {
        return this.#width;
    }

    override set width(value: number) {
        if (this.#width === value) {
            return;
        }
        this.#width = value;
        this.#renderer.setSize(this.#width, this.#height);
        this.#invalidateRaster();
        this.requiresRedraw = true;
    }

    override get height() {
        return this.#height;
    }

    override set height(value: number) {
        if (this.#height === value) {
            return;
        }
        this.#height = value;
        this.#renderer.setSize(this.#width, this.#height);
        this.#invalidateRaster();
        this.requiresRedraw = true;
    }

    get maxWidth() {
        return this.#width;
    }

    set maxWidth(value: number) {
        this.width = value;
    }

    get maxHeight() {
        return this.#height;
    }

    set maxHeight(value: number) {
        this.height = value;
    }

    /** The raw composition HTML, written by the external authoring layer. */
    get html(): string {
        return this.#html;
    }

    set html(value: string) {
        if (this.#html === value) {
            return;
        }
        this.#html = value;
        this.#invalidateManagedHtml();
        this.requiresRedraw = true;
    }

    /**
     * Lint findings for the current composition: static (HTML-source) rules plus runtime rules that
     * inspect the loaded composition. Async because the runtime rules need the iframe loaded.
     */
    async getLintFindings(): Promise<LintFinding[]> {
        if (!this.#html) {
            return [];
        }
        const staticFindings = lintHtmlComposition(this.#html);
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            const runtimeFindings = context ? runRuntimeLintRules(context) : [];
            return [...staticFindings, ...runtimeFindings];
        });
    }

    /**
     * Run an introspection task — which loads the shared iframe and may seek its timeline to measure
     * — on the same serialized queue as rasterization, so it never mutates the iframe pose while a
     * `snapdom` rasterize is mid-flight (and vice-versa).
     */
    async #serializeIntrospection<T>(task: () => Promise<T>): Promise<T> {
        const run = this.#rasterizeChain.then(task);
        this.#rasterizeChain = run.then(
            () => undefined,
            () => undefined,
        );
        return run;
    }

    /**
     * Replace an editable node's text (by `id`) via the `html` setter, so it re-rasters and — living
     * in `#html`, the source of truth — survives save/load and later agent revisions.
     */
    setEditableText(id: string, value: string): void {
        this.html = replaceEditableText(this.#html, id, value);
    }

    /**
     * Normalized progress (0..1) of the deterministic preview/edit frame: the midpoint of the
     * window where all editable nodes are co-visible. Falls back to 0.5 (a poster frame) when the
     * composition has no editable nodes or no shared visible window.
     */
    async getPreviewProgress(): Promise<number> {
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context || !context.timeline) {
                return 0.5;
            }
            return (
                computeEditableVisibility(context.document, context.timeline).editProgress ?? 0.5
            );
        });
    }

    /**
     * The editable text nodes with their text and bounds, measured at the preview/edit frame (where
     * all editable nodes are co-visible). Bounds are in reference-resolution space; the editor maps
     * them to screen space. Restores the playhead so it doesn't disturb the visible render.
     */
    async getEditableTextRegions(): Promise<EditableTextRegion[]> {
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context || !context.timeline) {
                return [];
            }
            const { document, timeline } = context;
            const progress = computeEditableVisibility(document, timeline).editProgress ?? 0.5;
            const originalTime = timeline.time();
            timeline.seek(progress * timeline.duration(), false);
            const regions = Array.from(document.querySelectorAll('[data-l5-editable]')).map(
                (node) => {
                    const rect = node.getBoundingClientRect();
                    return {
                        id: node.id,
                        text: node.textContent ?? '',
                        bounds: {
                            x: rect.left,
                            y: rect.top,
                            width: rect.width,
                            height: rect.height,
                        },
                    };
                },
            );
            timeline.seek(originalTime, true);
            return regions;
        });
    }

    /**
     * Toggle text-editing mode. While active, editable glyphs are hidden (`color: transparent`,
     * layout preserved) so the editor's overlay is the only visible copy of the text. Re-manages
     * and re-rasters the current frame exactly once per toggle.
     */
    async setTextEditing(active: boolean): Promise<void> {
        if (this.#textEditing === active) {
            return;
        }
        this.#textEditing = active;
        this.#invalidateManagedHtml();
        this.requiresRedraw = true;
        await this.#rasterizeAtProgress(this.#progress);
    }

    get hasHtmlTextEditingEnabled(): boolean {
        return this.#htmlTextEditManager !== null;
    }

    /**
     * Enter in-place text editing: hide the raster glyphs and inject a `contentEditable` overlay per
     * editable region into `domContainer` (positioned/styled to match the raster, keyed by id). The
     * caller (the editor) should have seeked to the preview/edit frame first. Edits commit on blur.
     */
    async enableHtmlTextInput(domContainer: HTMLElement): Promise<void> {
        if (this.#htmlTextEditManager) {
            await this.disableHtmlTextInput();
        }

        const regions = await this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context || !context.timeline) {
                return null;
            }
            const { document: doc, timeline } = context;
            const view = doc.defaultView;
            const editProgress = computeEditableVisibility(doc, timeline).editProgress ?? 0.5;

            const originalTime = timeline.time();
            timeline.seek(editProgress * timeline.duration(), false);
            const measured = Array.from(doc.querySelectorAll('[data-l5-editable]')).map((node) => {
                const rect = node.getBoundingClientRect();
                const computed = view?.getComputedStyle(node);
                const style: Record<string, string> = {};
                for (const prop of EDITABLE_OVERLAY_STYLE_PROPS) {
                    style[prop] = (computed as unknown as Record<string, string>)?.[prop] ?? '';
                }
                return {
                    id: node.id,
                    text: node.textContent ?? '',
                    bounds: { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
                    style,
                };
            });
            timeline.seek(originalTime, true);
            return measured;
        });

        if (!regions) {
            return;
        }

        await this.setTextEditing(true);

        const bounds = this.approximateMaxBounds;
        this.#htmlTextEditManager = new HtmlTextEditManager(domContainer, {
            assetBounds: { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height },
            referenceResolution: this.getReferenceResolution(),
            regions,
            commit: (id, value) => this.setEditableText(id, value),
        });
    }

    /** Exit text editing: commit pending edits, tear down the overlays, and restore the raster glyphs. */
    async disableHtmlTextInput(): Promise<void> {
        if (!this.#htmlTextEditManager) {
            return;
        }
        // Re-raster with glyphs restored *before* removing the overlays, so the canvas already shows
        // the text when they disappear (no flash of empty text).
        this.#htmlTextEditManager.commitAll();
        await this.setTextEditing(false);
        this.#htmlTextEditManager.removeOverlays();
        this.#htmlTextEditManager = null;
    }

    // Ensure the composition is loaded into the iframe and hand back its document + raw timeline for
    // runtime introspection (lint, NTE). Null when there's nothing to load.
    async #introspectionContext(): Promise<{
        document: Document;
        timeline: GsapTimelineLike | null;
    } | null> {
        this.#refreshManagedHtml();
        if (!this.#managedHtml) {
            return null;
        }
        await this.#loadManagedHtml();
        const document = this.#renderer.contentDocument;
        if (!document) {
            return null;
        }
        return {
            document,
            timeline: this.#renderer.getTimelineObject() as GsapTimelineLike | null,
        };
    }

    /**
     * The auto-managed HTML actually loaded into the iframe (template colors injected, etc.).
     */
    get managedHtml(): string {
        this.#refreshManagedHtml();
        return this.#managedHtml;
    }

    override _hasTemplateReferenceValues(): boolean {
        return new RegExp(`var\\(\\s*${TEMPLATE_COLOR_VAR_PREFIX}`).test(this.#html);
    }

    getVisibleResolution(): { width: number; height: number } {
        return { width: this.#width, height: this.#height };
    }

    override get approximateMaxBounds(): PIXI.Rectangle {
        const freeParent = this.getFreelyPositionedParent();
        const transformation = freeParent.transformation;
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(transformation, width, height);
        return { x, y, width, height } as PIXI.Rectangle;
    }

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(
            this.transformation.save() as TransformationType,
            width,
            height,
        );

        this._pixiContainer.x = x + offsetX;
        this._pixiContainer.y = y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
    }

    #getPalette(): string[] {
        const colors: string[] = [];
        if (this._templateOrNull?.colors) {
            for (const templateColor of this.template.colors) {
                colors.push(templateColorToHex(templateColor.color));
            }
        }
        return colors;
    }

    #invalidateManagedHtml(): void {
        this.#managedHtml = '';
        this.#managedSourceHtml = null;
        this.#loadedManagedHtml = null;
        this.#invalidateRaster();
    }

    #invalidateRaster(): void {
        // Keep #displayedCanvas so the last frame holds until the next buffer/render replaces it.
        this.#preparedCanvas = null;
        this.#preparedFrameIndex = null;
        this.#pendingFrameIndex = null;
    }

    #refreshManagedHtml(): boolean {
        if (!this.#html) {
            const had = this.#managedHtml !== '';
            this.#managedHtml = '';
            this.#managedSourceHtml = null;
            return had;
        }
        const palette = this.#getPalette();
        const paletteKey = palette.join('|');
        if (
            this.#managedSourceHtml === this.#html &&
            this.#paletteKey === paletteKey &&
            this.#managedTextEditing === this.#textEditing &&
            this.#managedHtml
        ) {
            return false;
        }
        this.#managedHtml = manageComposition(this.#html, palette, {
            textEditing: this.#textEditing,
        });
        this.#managedSourceHtml = this.#html;
        this.#paletteKey = paletteKey;
        this.#managedTextEditing = this.#textEditing;
        return true;
    }

    get #durationSeconds(): number {
        return (this.container?.duration ?? 0) / 1000;
    }

    #frameIndexAtProgress(progress: number): number {
        return Math.max(0, Math.floor(progress * this.#durationSeconds * PLAYBACK_RASTER_FPS));
    }

    #progressForFrameIndex(frame: number): number {
        const durationS = this.#durationSeconds;
        if (durationS <= 0) {
            return 0;
        }
        return clamp01(frame / PLAYBACK_RASTER_FPS / durationS);
    }

    async #loadManagedHtml(): Promise<void> {
        if (this.#loadedManagedHtml === this.#managedHtml) {
            return;
        }
        this.#renderer.setEmbedFonts(/@font-face/i.test(this.#managedHtml));
        this.#renderer.setSize(this.#width, this.#height);
        await this.#renderer.load(this.#managedHtml);
        this.#loadedManagedHtml = this.#managedHtml;
    }

    // Fire-and-forget prefetch of the next playback frame; updateForRender promotes it when reached.
    #prefetchFrame(frame: number): void {
        if (this.#pendingFrameIndex === frame) {
            return;
        }
        if (this.#preparedFrameIndex === frame && this.#preparedCanvas) {
            return;
        }
        this.#pendingFrameIndex = frame;
        const run = this.#rasterizeChain.then(async () => this.#prepareFrame(frame));
        this.#rasterizeChain = run.catch(() => undefined);
    }

    // Seek + rasterize at output density (shared by prefetch and the awaited buffer path); null when
    // there's nothing to render or the asset was torn down mid-rasterize.
    async #seekAndRasterize(progress: number): Promise<HTMLCanvasElement | null> {
        this.#refreshManagedHtml();
        if (!this.#managedHtml) {
            return null;
        }
        await this.#loadManagedHtml();
        this.#renderer.seek(progress);
        const canvas = await this.#renderer.rasterize(this.referenceToOutputMultiplier);
        return this.destroyed ? null : canvas;
    }

    async #prepareFrame(frame: number): Promise<void> {
        // #pendingFrameIndex !== frame means a newer prefetch superseded this one.
        if (this.#pendingFrameIndex !== frame) {
            return;
        }
        try {
            const canvas = await this.#seekAndRasterize(this.#progressForFrameIndex(frame));
            if (canvas && this.#pendingFrameIndex === frame) {
                this.#preparedCanvas = canvas;
                this.#preparedFrameIndex = frame;
            }
        } catch {
            // A failed rasterization holds the last frame; retried on the next prefetch.
        } finally {
            if (this.#pendingFrameIndex === frame) {
                this.#pendingFrameIndex = null;
            }
        }
    }

    // The awaited path for seeks and export: rasterize `progress` into the displayed-frame slot,
    // which the next updateForRender blits.
    async #rasterizeAtProgress(progress: number): Promise<void> {
        const run = this.#rasterizeChain.then(async () => {
            try {
                const canvas = await this.#seekAndRasterize(progress);
                if (!canvas) {
                    return;
                }
                this.#displayedCanvas = canvas;
                this.#preparedCanvas = null;
                this.#preparedFrameIndex = null;
            } catch {
                // Hold the last displayed frame; the editor re-buffers on the next seek.
            }
        });
        this.#rasterizeChain = run.catch(() => undefined);
        return run;
    }

    override async _bufferAtTime(
        time: number,
        bufferingMode = BufferingMode.ResolveWhenSingleFrame,
    ): Promise<void> {
        if (!this._shouldLoad || !this.#html) {
            return;
        }
        if (bufferingMode === BufferingMode.PrepareForPlayback) {
            return;
        }
        const duration = this.container?.duration;
        const progress = duration && duration > 0 ? clamp01(time / duration) : this.#progress;
        this.#progress = progress;
        await this.#rasterizeAtProgress(progress);
    }

    override updateForRender() {
        const frameIndex = this.#frameIndexAtProgress(this.#progress);
        if (this.#preparedFrameIndex === frameIndex && this.#preparedCanvas) {
            this.#displayedCanvas = this.#preparedCanvas;
            this.#preparedCanvas = null;
            this.#preparedFrameIndex = null;
        }
        if (!this.visible || !this.#displayedCanvas) {
            return;
        }

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const sharedTexture = this.#sprite.texture;
        const canvas = (sharedTexture.baseTexture.resource as PIXI.BaseImageResource)
            .source as HTMLCanvasElement;

        // The shared texture is video-frame sized, not asset sized; fit the asset's aspect ratio
        // into it (mirroring ChartAsset) so the composition isn't stretched.
        const assetAspect = this.#width / this.#height;
        const baseTexW = sharedTexture.width;
        const baseTexH = sharedTexture.height;
        let frameW: number;
        let frameH: number;
        if (baseTexW / baseTexH > assetAspect) {
            frameH = baseTexH;
            frameW = frameH * assetAspect;
        } else {
            frameW = baseTexW;
            frameH = frameW / assetAspect;
        }
        const canvasScale = canvas.width / baseTexW;
        const destWidthPx = frameW * canvasScale;
        const destHeightPx = frameH * canvasScale;

        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.resetTransform();
        ctx?.drawImage(this.#displayedCanvas, 0, 0, destWidthPx, destHeightPx);

        const frame = new PIXI.Rectangle(0, 0, frameW, frameH);
        sharedTexture.frame = frame;
        sharedTexture.orig = frame;
        sharedTexture.trim = frame;

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;

        sharedTexture.update();
    }

    override _build() {
        this.clearAnimation();
        super._build();
        // Asset-level animation only; the composition self-animates via progress, and dynamic
        // styling (restyling/recoloring content) doesn't apply to an opaque raster.
        this.applyTransformation();
        this._applyAnimation();
        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        super._seek(progress, seekMode);
        this.#progress = clamp01(progress);
        // Playback advances the playhead without buffering, so prefetch the next frame ahead of its
        // tick. Seeks/export go through the awaited _bufferAtTime instead.
        if (this.#html && this.luminaryParent?.isPlaying) {
            this.#prefetchFrame(this.#frameIndexAtProgress(this.#progress) + 1);
        }
    }

    _htmlCustomSave(): string {
        return this.#html;
    }

    _htmlCustomLoad(data: string | null, _settings: LoadSettings) {
        this.#html = data ?? '';
        this.#invalidateManagedHtml();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['mask', 'maxWidth', 'maxHeight', 'html']);
    }
}
